import { useState, useEffect } from 'react';
import { useMeetingStore } from '../../../store/meetingStore';
import { Card, CardHeader, CardContent } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import { Plus, Search, Video, Calendar, Clock, MapPin, Trash2 } from 'lucide-react';
import { Input } from '../../../components/Input';
import { Textarea } from '../../../components/Textarea';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../components/Dialog';
import toast from 'react-hot-toast';

const nowLocal = (offsetH: number) => {
    const d = new Date(Date.now() + offsetH * 3600_000);
    d.setSeconds(0, 0);
    // format for <input type="datetime-local"> (local time)
    const tz = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tz).toISOString().slice(0, 16);
};
const emptyForm = () => ({ title: '', description: '', startTime: nowLocal(1), endTime: nowLocal(2), location: '', meetingUrl: '', status: 'scheduled' });

export function Meetings() {
    const { meetings, isLoading, fetchMeetings, createMeeting, deleteMeeting } = useMeetingStore();
    const [search, setSearch] = useState('');
    const [isFormOpen, setFormOpen] = useState(false);
    const [form, setForm] = useState(emptyForm());
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        fetchMeetings();
    }, []);

    const filteredMeetings = meetings.filter(m =>
        m.title.toLowerCase().includes(search.toLowerCase())
    );

    const openCreate = () => { setForm(emptyForm()); setErrors({}); setFormOpen(true); };

    const handleSubmit = async () => {
        const next: Record<string, string> = {};
        if (!form.title.trim()) next.title = 'Title is required';
        if (!form.startTime) next.startTime = 'Start time is required';
        if (!form.endTime) next.endTime = 'End time is required';
        if (form.startTime && form.endTime && form.endTime <= form.startTime) next.endTime = 'End must be after start';
        setErrors(next);
        if (Object.keys(next).length) return;
        setSaving(true);
        try {
            await createMeeting({
                title: form.title.trim(),
                description: form.description.trim(),
                startTime: new Date(form.startTime).toISOString(),
                endTime: new Date(form.endTime).toISOString(),
                location: form.location.trim(),
                meetingUrl: form.meetingUrl.trim(),
                status: form.status,
            });
            toast.success('Meeting scheduled');
            setFormOpen(false);
        } catch (e) {
            toast.error('Failed to schedule meeting');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await deleteMeeting(deleteTarget.id);
            toast.success('Meeting deleted');
            setDeleteTarget(null);
        } catch (e) {
            toast.error('Failed to delete meeting');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="tracking-tight text-foreground font-display text-page-title">Meetings</h1>
                    <p className="text-muted-foreground">Schedule, record, and track your team syncs seamlessly.</p>
                </div>
                <Button variant="primary" onClick={openCreate}>
                    <Plus className="w-4 h-4 mr-2" />
                    New Meeting
                </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                            <Video className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-muted-foreground">Total Meetings</p>
                            <h3 className="text-foreground font-display text-metric">
                                {meetings.length}
                            </h3>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center text-warning">
                            <Clock className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-muted-foreground">Upcoming</p>
                            <h3 className="text-foreground font-display text-metric">
                                {meetings.filter(m => m.status === 'scheduled').length}
                            </h3>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center text-success">
                            <Calendar className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-muted-foreground">Completed</p>
                            <h3 className="text-foreground font-display text-metric">
                                {meetings.filter(m => m.status === 'completed').length}
                            </h3>
                        </div>
                    </CardContent>
                </Card>
            </div>

            <Card>
                <CardHeader>
                    <div className="flex justify-between items-center">
                        <h3 className="text-foreground font-display text-section-title">Meeting List</h3>
                        <div className="relative w-64">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input 
                                placeholder="Search meetings..." 
                                className="pl-9"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    {isLoading ? (
                        <div className="p-8 text-center text-muted-foreground">Loading...</div>
                    ) : (
                        <table className="w-full text-sm text-left">
                            <thead className="bg-secondary/50 text-muted-foreground font-semibold border-y border-border">
                                <tr>
                                    <th className="px-6 py-3">Meeting Title</th>
                                    <th className="px-6 py-3">Start Time</th>
                                    <th className="px-6 py-3">End Time</th>
                                    <th className="px-6 py-3">Location</th>
                                    <th className="px-6 py-3">Status</th>
                                    <th className="px-6 py-3 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {filteredMeetings.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="px-6 py-8 text-center text-muted-foreground">
                                            No meetings found.
                                        </td>
                                    </tr>
                                ) : filteredMeetings.map(m => (
                                    <tr key={m.id} className="hover:bg-secondary/20 transition-colors">
                                        <td className="px-6 py-4 font-medium text-foreground">
                                            {m.title}
                                            <div className="text-xs text-muted-foreground font-normal line-clamp-1">{m.description}</div>
                                        </td>
                                        <td className="px-6 py-4">
                                            {new Date(m.startTime).toLocaleString()}
                                        </td>
                                        <td className="px-6 py-4">
                                            {new Date(m.endTime).toLocaleTimeString()}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-1 text-muted-foreground">
                                                <MapPin className="w-3 h-3" />
                                                <span>{m.location || 'N/A'}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <Badge variant={m.status === 'completed' ? 'success' : m.status === 'scheduled' ? 'primary' : 'secondary'}>
                                                {m.status.toUpperCase()}
                                            </Badge>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <Button variant="ghost" size="icon-sm" aria-label="Delete meeting"
                                                className="text-muted-foreground hover:text-destructive"
                                                onClick={() => setDeleteTarget({ id: m.id, title: m.title })}>
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </CardContent>
            </Card>

            {/* Schedule meeting modal */}
            <Dialog open={isFormOpen} onOpenChange={setFormOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>New Meeting</DialogTitle>
                        <DialogDescription>Schedule a team sync or call.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 max-h-[62vh] overflow-y-auto pr-1">
                        <div className="space-y-1.5">
                            <label className="text-sm font-medium text-foreground">Title</label>
                            <Input value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
                                placeholder="e.g. Weekly Sync" invalid={!!errors.title} />
                            {errors.title && <p className="text-xs text-destructive">{errors.title}</p>}
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-sm font-medium text-foreground">Description</label>
                            <Textarea rows={2} value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                                placeholder="Agenda / notes…" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <label className="text-sm font-medium text-foreground">Start</label>
                                <Input type="datetime-local" value={form.startTime} onChange={(e) => setForm(f => ({ ...f, startTime: e.target.value }))} invalid={!!errors.startTime} />
                                {errors.startTime && <p className="text-xs text-destructive">{errors.startTime}</p>}
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-sm font-medium text-foreground">End</label>
                                <Input type="datetime-local" value={form.endTime} onChange={(e) => setForm(f => ({ ...f, endTime: e.target.value }))} invalid={!!errors.endTime} />
                                {errors.endTime && <p className="text-xs text-destructive">{errors.endTime}</p>}
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <label className="text-sm font-medium text-foreground">Location</label>
                                <Input value={form.location} onChange={(e) => setForm(f => ({ ...f, location: e.target.value }))} placeholder="Room / platform" />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-sm font-medium text-foreground">Meeting URL</label>
                                <Input value={form.meetingUrl} onChange={(e) => setForm(f => ({ ...f, meetingUrl: e.target.value }))} placeholder="https://…" />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="secondary" onClick={() => setFormOpen(false)} disabled={saving}>Cancel</Button>
                        <Button variant="primary" onClick={handleSubmit} isLoading={saving}>Schedule</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete confirm */}
            <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
                <DialogContent className="max-w-[440px]">
                    <DialogHeader>
                        <DialogTitle>Delete meeting</DialogTitle>
                        <DialogDescription>
                            Delete <span className="font-medium text-foreground">{deleteTarget?.title}</span>? This cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
                        <Button variant="destructive" onClick={handleDelete} isLoading={deleting}>Delete</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
